import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../api/endpoints';

// Browser-Telefonie über das Twilio Voice JS SDK.
// - Ausgehend (Click-to-Call): Das System ruft zuerst den Mitarbeiter im Browser an und verbindet dann mit dem
//   Kunden (feste Firmennummer). Dieser Anruf wird automatisch angenommen – aber nur, wenn der Mitarbeiter ihn
//   gerade selbst gestartet hat.
// - Eingehend (Rückruf auf die Firmennummer): Es klingelt bei allen Telefonisten gleichzeitig; angenommen wird
//   bewusst per Klick. Wer zuerst annimmt, hat das Gespräch, bei den anderen hört es auf zu klingeln.

export type BrowserPhoneState = 'off' | 'starting' | 'ready' | 'ringing' | 'incoming' | 'in-call' | 'error';

// Eingehender Anruf (Parameter aus der TwiML des Servers).
export interface IncomingCall {
  callId: string;
  caller: string;
  leadId?: string;
  leadName?: string;
}

// Minimaler Ausschnitt der SDK-Typen (erleichtert Tests mit einem nachgebauten Gerät).
export interface PhoneCall {
  accept(): void;
  reject(): void;
  disconnect(): void;
  mute(muted: boolean): void;
  on(event: 'disconnect' | 'cancel' | 'reject' | 'error', fn: () => void): void;
  customParameters?: Map<string, string>;
}

export interface PhoneDevice {
  register(): Promise<void>;
  updateToken(token: string): void;
  destroy(): void;
  on(event: 'registered' | 'unregistered', fn: () => void): void;
  on(event: 'error', fn: (e: { message?: string; code?: number }) => void): void;
  on(event: 'incoming', fn: (call: PhoneCall) => void): void;
  on(event: 'tokenWillExpire', fn: () => void): void;
}

export type DeviceFactory = (token: string) => Promise<PhoneDevice>;

// Das SDK wird erst geladen, wenn Browser-Telefonie tatsächlich genutzt wird (eigener Chunk).
const defaultFactory: DeviceFactory = async (token) => {
  const { Device } = await import('@twilio/voice-sdk');
  return new Device(token, {
    edge: ['frankfurt', 'dublin'],
    closeProtection: 'Ein Anruf läuft noch. Seite wirklich verlassen?',
    codecPreferences: ['opus', 'pcmu'] as never,
    logLevel: 'error',
  }) as unknown as PhoneDevice;
};

// Wie lange nach "Anruf starten" ein eingehender Anruf automatisch angenommen wird.
const EXPECT_WINDOW_MS = 45_000;

type Phase = 'idle' | 'ready' | 'ringing' | 'incoming' | 'in-call' | 'error';

export function useBrowserPhone(projectId: string, enabled: boolean, factory: DeviceFactory = defaultFactory) {
  const [phase, setState] = useState<Phase>('idle');
  const [error, setError] = useState<string>();
  const [muted, setMuted] = useState(false);
  const [incoming, setIncoming] = useState<IncomingCall>();
  const device = useRef<PhoneDevice | null>(null);
  const call = useRef<PhoneCall | null>(null);
  const pending = useRef<PhoneCall | null>(null);
  const expectUntil = useRef(0);
  const state: BrowserPhoneState = !enabled ? 'off' : phase === 'idle' ? 'starting' : phase;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        // Mikrofon früh anfragen: Ohne Freigabe kann kein Gespräch zustande kommen.
        const stream = await navigator.mediaDevices?.getUserMedia({ audio: true });
        stream?.getTracks().forEach((t) => t.stop());
        const { token } = await api.browserToken(projectId);
        if (cancelled) return;
        const d = await factory(token);
        if (cancelled) {
          d.destroy();
          return;
        }
        device.current = d;
        const idle = (s: Phase): Phase => (s === 'in-call' || s === 'ringing' || s === 'incoming' ? s : 'ready');
        d.on('registered', () => setState(idle));
        d.on('unregistered', () => setState((s) => (s === 'in-call' ? s : 'idle')));
        d.on('error', (e) => {
          setError(e.message ?? 'Fehler der Browser-Telefonie');
          setState((s) => (s === 'in-call' ? s : 'error'));
        });
        d.on('tokenWillExpire', () => {
          void api.browserToken(projectId).then(({ token: t }) => d.updateToken(t)).catch(() => undefined);
        });
        const track = (c: PhoneCall) => {
          const done = () => {
            if (call.current === c) call.current = null;
            if (pending.current === c) {
              pending.current = null;
              setIncoming(undefined);
            }
            setMuted(false);
            setState((s) => (call.current || pending.current ? s : 'ready'));
          };
          c.on('disconnect', done);
          c.on('cancel', done);
          c.on('reject', done);
          c.on('error', done);
        };
        d.on('incoming', (c) => {
          const params = c.customParameters;
          if (params?.get('direction') === 'inbound') {
            // Rückruf eines Kunden: klingeln lassen, der Mitarbeiter nimmt per Klick an (nicht während eines Gesprächs).
            if (call.current || pending.current) {
              c.reject();
              return;
            }
            pending.current = c;
            track(c);
            setIncoming({
              callId: params.get('callId') ?? '',
              caller: params.get('caller') ?? '',
              leadId: params.get('leadId') || undefined,
              leadName: params.get('leadName') || undefined,
            });
            setState('incoming');
            return;
          }
          // Mitarbeiter-Bein eines selbst gestarteten Anrufs: automatisch annehmen; alles andere ablehnen.
          if (Date.now() > expectUntil.current || call.current) {
            c.reject();
            return;
          }
          expectUntil.current = 0;
          call.current = c;
          track(c);
          c.accept();
          setState('in-call');
        });
        await d.register();
      } catch (e) {
        if (cancelled) return;
        const name = (e as { name?: string }).name;
        setError(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'Bitte den Mikrofonzugriff im Browser erlauben.'
            : name === 'NotFoundError'
              ? 'Kein Mikrofon gefunden. Bitte ein Headset anschließen.'
              : ((e as Error).message ?? 'Browser-Telefonie konnte nicht gestartet werden.'),
        );
        setState('error');
      }
    })();
    return () => {
      cancelled = true;
      call.current?.disconnect();
      pending.current?.reject();
      call.current = null;
      pending.current = null;
      device.current?.destroy();
      device.current = null;
      setState('idle');
      setError(undefined);
      setIncoming(undefined);
    };
  }, [enabled, projectId, factory]);

  // expect: vor "Anruf starten" aufrufen – der gleich eingehende Mitarbeiter-Anruf wird automatisch angenommen.
  const expect = useCallback(() => {
    expectUntil.current = Date.now() + EXPECT_WINDOW_MS;
    setState((s) => (s === 'ready' ? 'ringing' : s));
  }, []);
  const cancelExpect = useCallback(() => {
    expectUntil.current = 0;
    setState((s) => (s === 'ringing' ? 'ready' : s));
  }, []);
  // accept: eingehenden Rückruf annehmen; liefert die Angaben zum Anruf (z. B. um den Lead zu öffnen).
  const accept = useCallback((): IncomingCall | undefined => {
    const c = pending.current;
    if (!c) return undefined;
    pending.current = null;
    call.current = c;
    c.accept();
    const info = incoming;
    setIncoming(undefined);
    setState('in-call');
    return info;
  }, [incoming]);
  const reject = useCallback(() => {
    pending.current?.reject();
  }, []);
  const hangup = useCallback(() => call.current?.disconnect(), []);
  const toggleMute = useCallback(() => {
    if (!call.current) return;
    setMuted((m) => {
      call.current?.mute(!m);
      return !m;
    });
  }, []);

  return { state, error, muted, incoming, expect, cancelExpect, accept, reject, hangup, toggleMute };
}

export type BrowserPhone = ReturnType<typeof useBrowserPhone>;
